<template>
  <!-- Reusable FKE AI summary block (OKV-234): animated multicolor border, a title, skeleton while
       loading, the genAnswer rendered as Markdown, copy-to-clipboard (no feedback — backend-cached)
       and the AI disclaimer. Gated by the FKE_SUMMARY_PERMISSION role; renders nothing without it.
       Each usage passes its own title/query/cacheKey; loading starts when `active` becomes true. -->
  <div v-if="canView && (loading || error || text)" class="fke-narrative-summary">
    <div class="fke-narrative-summary__title">{{ title }}</div>

    <template v-if="loading">
      <ul v-if="reasoningLines.length" class="fke-narrative-summary__reasoning">
        <li v-for="(line, i) in reasoningLines" :key="i" class="fke-narrative-summary__reasoning-line">
          {{ line }}
        </li>
      </ul>
      <b-skeleton width="95%"></b-skeleton>
      <b-skeleton width="100%"></b-skeleton>
      <b-skeleton width="90%"></b-skeleton>
      <b-skeleton width="55%"></b-skeleton>
    </template>

    <p v-else-if="error" class="text-muted mb-0">{{ $t('fkeSummary.error') }}</p>

    <template v-else>
      <!-- eslint-disable-next-line vue/no-v-html — input is HTML-escaped then rebuilt from a fixed tag set in utils/markdown.js -->
      <div class="fke-narrative-summary__text" v-html="html"></div>
      <div class="fke-narrative-summary__footer">
        <p class="fke-narrative-summary__disclaimer">{{ $t('fkeSummary.aiDisclaimer') }}</p>
        <button type="button" class="fke-narrative-summary__copy" @click.stop="copy">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
          </svg>
          {{ $t('fkeSummary.copy') }}
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import { renderMarkdown } from '../../utils/markdown'
import {
  FKE_SUMMARY_PERMISSION,
  fetchFkeSummary,
  fetchFkeDocuments,
  readFkeSummaryCache,
  writeFkeSummaryCache,
} from '../../utils/fkeSummary'

// Pacing of the "reasoning" placeholder lines shown while the slow genAnswer runs.
const REASONING_STEP_MS = 4500
const REASONING_MAX_MS = 60000
const REASONING_MAX_DOCS = 4

export default {
  name: 'FkeNarrativeSummary',
  props: {
    // Internal heading, e.g. "Descrizione dei Peer" / "Descrizione prodotto". Caller resolves i18n.
    title: {
      type: String,
      default: '',
    },
    // The FKE query — different per usage.
    query: {
      type: String,
      default: '',
    },
    // Document revisions the answer is built from (filters.revisionId.in).
    revisionIds: {
      type: Array,
      default: () => [],
    },
    // Base cache key for this usage (e.g. "peer.<testId>"); language is appended internally so the
    // same location is cached per language. Caller must make it unique per location/subject.
    cacheKey: {
      type: String,
      default: '',
    },
    // Loading starts when this turns true (collapsible open, page mounted, …). Set true to load now.
    active: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      error: false,
      text: '',
      // Placeholder "reasoning" lines revealed one by one while genAnswer runs (see startReasoning).
      reasoningLines: [],
    }
  },
  created() {
    // Non-reactive helpers for the reasoning ticker.
    this._reasoningTimer = null
    this._reasoningQueue = []
    this._reasoningStart = 0
  },
  computed: {
    // Same gating permission for every FKE summary instance.
    canView() {
      return this.$store.getters.can(FKE_SUMMARY_PERMISSION)
    },
    html() {
      return renderMarkdown(this.text)
    },
  },
  watch: {
    active() {
      this.maybeLoad()
    },
    revisionIds() {
      this.maybeLoad()
    },
  },
  beforeDestroy() {
    this.stopReasoning()
  },
  mounted() {
    this.maybeLoad()
  },
  methods: {
    // Load once per mount: skip without permission, while inactive, when already loading, or once an
    // answer is present. Order per the ticket: permission → cache → request.
    maybeLoad() {
      if (!this.active || !this.canView || this.loading || this.text) return
      const language = this.$i18n.locale
      const cached = readFkeSummaryCache(this.cacheKey, language)
      if (cached) {
        this.text = cached
        return
      }
      if (this.revisionIds.length === 0) return
      this.loading = true
      this.error = false
      this.startReasoning()
      // Fast parallel metadata lookup over the same documents — feeds the reasoning lines while the
      // slow genAnswer runs. Best-effort: its failure must not affect the main flow.
      fetchFkeDocuments({ revisionIds: this.revisionIds })
        .then((info) => {
          if (this.loading) this.enrichReasoning(info)
        })
        .catch(() => {})
      fetchFkeSummary({ query: this.query, revisionIds: this.revisionIds, language })
        .then((text) => {
          this.text = text
          this.loading = false
          this.stopReasoning()
          writeFkeSummaryCache(this.cacheKey, language, text)
        })
        .catch(() => {
          this.loading = false
          this.stopReasoning()
          this.error = true
        })
    },
    // ── "Reasoning" placeholder ticker ──────────────────────────────────────────
    // Reveals short pseudo-reasoning lines one by one (generic openers first, then lines built from
    // the documents:filter metadata, then rotating fillers) to entertain the ~minute-long wait.
    startReasoning() {
      this.reasoningLines = []
      this._reasoningQueue = [
        this.$t('fkeSummary.reasoning.start'),
        this.$t('fkeSummary.reasoning.fetchingDocs'),
      ]
      this._reasoningStart = Date.now()
      this._revealNextReasoning()
      this._reasoningTimer = setInterval(this._revealNextReasoning, REASONING_STEP_MS)
    },
    _revealNextReasoning() {
      if (!this.loading || Date.now() - this._reasoningStart > REASONING_MAX_MS) return
      if (this._reasoningQueue.length) {
        this.reasoningLines.push(this._reasoningQueue.shift())
        return
      }
      const fillers = [
        this.$t('fkeSummary.reasoning.checkRelevant'),
        this.$t('fkeSummary.reasoning.extracting'),
        this.$t('fkeSummary.reasoning.composing'),
        this.$t('fkeSummary.reasoning.almost'),
      ]
      this.reasoningLines.push(fillers[this.reasoningLines.length % fillers.length])
    },
    enrichReasoning({ totalItems, documents }) {
      const lines = [this.$tc('fkeSummary.reasoning.foundDocs', totalItems || 0, { count: totalItems || 0 })]
      ;(documents || []).slice(0, REASONING_MAX_DOCS).forEach((doc) => {
        lines.push(this._reasoningDocLine(doc))
        lines.push(this.$t('fkeSummary.reasoning.checkRelevant'))
      })
      lines.push(this.$t('fkeSummary.reasoning.extracting'))
      lines.push(this.$t('fkeSummary.reasoning.composing'))
      this._reasoningQueue.push(...lines)
    },
    _reasoningDocLine(doc) {
      const type = doc.businessType || this.$t('fkeSummary.reasoning.genericDoc')
      const bits = [doc.productName, doc.issuerName, String(doc.revisionDate || '').slice(0, 10)].filter(Boolean)
      const tail = bits.length ? ' — ' + bits.join(', ') : ''
      const lang = doc.language ? ' [' + doc.language.toUpperCase() + ']' : ''
      return this.$t('fkeSummary.reasoning.readingDoc', { type }) + tail + lang
    },
    stopReasoning() {
      if (this._reasoningTimer) {
        clearInterval(this._reasoningTimer)
        this._reasoningTimer = null
      }
      this._reasoningQueue = []
      this.reasoningLines = []
    },
    // Copy the raw answer text (not the rendered HTML). No confirmation feedback by design.
    copy() {
      if (this.text && navigator.clipboard) {
        navigator.clipboard.writeText(this.text)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// Animated multicolor (conic-gradient) border; `@property` lets the angle animate, browsers without
// it fall back to a static gradient border.
@property --ai-angle {
  syntax: '<angle>';
  inherits: false;
  initial-value: 0deg;
}
@keyframes ai-glow-spin {
  to {
    --ai-angle: 360deg;
  }
}

.fke-narrative-summary {
  background: linear-gradient(white, white) padding-box,
    conic-gradient(
        from var(--ai-angle),
        #a78bfa 0%,
        #f472b6 22%,
        #fb923c 44%,
        #34d399 66%,
        #60a5fa 88%,
        #a78bfa 100%
      )
      border-box;
  border: 1.5px solid transparent;
  border-radius: 8px;
  padding: 14px 16px;
  box-shadow: 0 0 0 3px rgba(167, 139, 250, 0.08), 0 2px 12px rgba(244, 114, 182, 0.08);
  animation: ai-glow-spin 5s linear infinite;
}

.fke-narrative-summary__title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #8b5cf6;
  margin-bottom: 10px;
}

// "Reasoning" ticker lines shown above the skeleton while genAnswer runs.
.fke-narrative-summary__reasoning {
  list-style: none;
  margin: 0 0 12px;
  padding: 0;
}
.fke-narrative-summary__reasoning-line {
  font-size: 12.5px;
  font-style: italic;
  color: #8a93a2;
  line-height: 1.6;
  padding-left: 16px;
  position: relative;
  animation: fke-reasoning-in 0.35s ease both;
}
.fke-narrative-summary__reasoning-line::before {
  content: '›';
  position: absolute;
  left: 2px;
  color: #a78bfa;
}
@keyframes fke-reasoning-in {
  from {
    opacity: 0;
    transform: translateY(3px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// v-html content is not touched by scoped CSS, so style its descendants via /deep/.
.fke-narrative-summary__text {
  font-size: 13.5px;
  color: #1e293b;
  line-height: 1.7;
}
.fke-narrative-summary__text /deep/ p {
  font-size: 13.5px;
  color: #1e293b;
  line-height: 1.7;
  margin: 0 0 8px;
}
.fke-narrative-summary__text /deep/ p:last-child {
  margin-bottom: 0;
}
.fke-narrative-summary__text /deep/ p.md-heading {
  font-weight: 700;
  color: #0f172a;
}
.fke-narrative-summary__text /deep/ ul,
.fke-narrative-summary__text /deep/ ol {
  margin: 0 0 8px;
  padding-left: 18px;
}
.fke-narrative-summary__text /deep/ li {
  font-size: 13px;
  color: #374151;
  line-height: 1.6;
  margin-bottom: 4px;
}
.fke-narrative-summary__text /deep/ a {
  color: #4f46e5;
}
.fke-narrative-summary__text /deep/ code {
  font-family: monospace;
  font-size: 12px;
  background: #f1f5f9;
  padding: 1px 4px;
  border-radius: 4px;
}

// Disclaimer on the left, copy button pinned bottom-right.
.fke-narrative-summary__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 12px;
}
.fke-narrative-summary__disclaimer {
  flex: 1;
  font-size: 11px;
  color: #808080;
  font-style: italic;
  text-align: left;
  margin: 0;
}
.fke-narrative-summary__copy {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 13px;
  border: 1px solid #e2e8f0;
  background: transparent;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  transition: background 0.12s;
}
.fke-narrative-summary__copy:hover {
  background: #f1f5f9;
}
</style>
